const DEFAULT_BLUR_RADIUS_PX = 26;
const DEFAULT_FRAME_RATE = 24;
const DEFAULT_SEGMENTATION_FRAME_RATE = 12;
const DEFAULT_SEGMENTATION_MAX_SIZE_PX = 512;
const DEFAULT_MASK_FEATHER_PX = 2.5;
const DEFAULT_MASK_SMOOTHING = 0.5;
const FOREGROUND_ALPHA_LOW = 0.42;
const FOREGROUND_ALPHA_HIGH = 0.76;
const FOREGROUND_ALPHA_GAMMA = 1.35;
const WASM_ASSET_PATH = '/vendor/mediapipe/tasks-vision/wasm';
const SELFIE_SEGMENTER_MODEL_PATH = '/vendor/mediapipe/models/selfie_segmenter.tflite';

export const VIDEO_BACKGROUND_EFFECT_NONE = 'none';
export const VIDEO_BACKGROUND_EFFECT_BLUR = 'blur';

let segmenterPromise = null;

const canUseVideoBackgroundProcessing = () =>
  typeof window !== 'undefined' &&
  typeof document !== 'undefined' &&
  typeof HTMLCanvasElement !== 'undefined' &&
  typeof HTMLCanvasElement.prototype.captureStream === 'function';

const getAssetUrl = (path) => {
  if (typeof window === 'undefined') return path;
  return new URL(path, window.location.origin).toString();
};

const loadSelfieSegmenter = async () => {
  if (!segmenterPromise) {
    segmenterPromise = import('@mediapipe/tasks-vision').then(async (vision) => {
      const wasmFileset = await vision.FilesetResolver.forVisionTasks(getAssetUrl(WASM_ASSET_PATH));
      return vision.ImageSegmenter.createFromOptions(wasmFileset, {
        baseOptions: {
          modelAssetPath: getAssetUrl(SELFIE_SEGMENTER_MODEL_PATH),
        },
        runningMode: 'VIDEO',
        outputCategoryMask: true,
        outputConfidenceMasks: true,
      });
    });
  }
  return segmenterPromise;
};

const waitForVideoReady = (video) =>
  new Promise((resolve, reject) => {
    if (video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0) {
      resolve();
      return;
    }
    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error('Camera video did not become ready for background processing'));
    }, 5000);
    const cleanup = () => {
      window.clearTimeout(timeout);
      video.removeEventListener('loadedmetadata', handleReady);
      video.removeEventListener('canplay', handleReady);
      video.removeEventListener('error', handleError);
    };
    const handleReady = () => {
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        cleanup();
        resolve();
      }
    };
    const handleError = () => {
      cleanup();
      reject(new Error('Camera video failed to start background processing'));
    };
    video.addEventListener('loadedmetadata', handleReady);
    video.addEventListener('canplay', handleReady);
    video.addEventListener('error', handleError);
  });

const resolveLabeledPersonIndex = (labels = []) => {
  if (!Array.isArray(labels)) return -1;
  const index = labels.findIndex((label) => /person|human|foreground/i.test(String(label || '')));
  return index;
};

const resizeCanvas = (canvas, width, height) => {
  const nextWidth = Math.max(1, Math.round(width || 0));
  const nextHeight = Math.max(1, Math.round(height || 0));
  if (canvas.width !== nextWidth) canvas.width = nextWidth;
  if (canvas.height !== nextHeight) canvas.height = nextHeight;
};

const getAnalysisSize = (width, height, maxSize) => {
  const safeWidth = Math.max(1, Math.round(width || 0));
  const safeHeight = Math.max(1, Math.round(height || 0));
  const safeMaxSize = Math.max(120, Math.round(maxSize || DEFAULT_SEGMENTATION_MAX_SIZE_PX));
  const longestSide = Math.max(safeWidth, safeHeight);
  if (longestSide <= safeMaxSize) {
    return { width: safeWidth, height: safeHeight };
  }
  const scale = safeMaxSize / longestSide;
  return {
    width: Math.max(1, Math.round(safeWidth * scale)),
    height: Math.max(1, Math.round(safeHeight * scale)),
  };
};

const getMaskSampleBoxes = (width, height) => {
  const centerLeft = Math.floor(width * 0.28);
  const centerRight = Math.ceil(width * 0.72);
  const centerTop = Math.floor(height * 0.2);
  const centerBottom = Math.ceil(height * 0.9);
  const edgeWidth = Math.max(1, Math.floor(width * 0.16));
  const edgeHeight = Math.max(1, Math.floor(height * 0.16));
  return {
    center: { left: centerLeft, right: centerRight, top: centerTop, bottom: centerBottom },
    edges: [
      { left: 0, right: width, top: 0, bottom: edgeHeight },
      { left: 0, right: width, top: height - edgeHeight, bottom: height },
      { left: 0, right: edgeWidth, top: 0, bottom: height },
      { left: width - edgeWidth, right: width, top: 0, bottom: height },
    ],
  };
};

const averageFloatMaskArea = (source, width, box) => {
  let total = 0;
  let count = 0;
  for (let y = box.top; y < box.bottom; y += 1) {
    for (let x = box.left; x < box.right; x += 1) {
      total += source[y * width + x] || 0;
      count += 1;
    }
  }
  return count > 0 ? total / count : 0;
};

const averageFloatMaskEdges = (source, width, boxes) => {
  const totals = boxes.map((box) => averageFloatMaskArea(source, width, box));
  if (!totals.length) return 0;
  return totals.reduce((sum, value) => sum + value, 0) / totals.length;
};

const getConfidenceMaskStats = (mask) => {
  const width = Math.max(1, Math.round(mask.width || 0));
  const height = Math.max(1, Math.round(mask.height || 0));
  const source = mask.getAsFloat32Array();
  const boxes = getMaskSampleBoxes(width, height);
  const centerAverage = averageFloatMaskArea(source, width, boxes.center);
  const edgeAverage = averageFloatMaskEdges(source, width, boxes.edges);
  return {
    centerAverage,
    edgeAverage,
    foregroundScore: centerAverage - edgeAverage,
    separation: Math.abs(centerAverage - edgeAverage),
  };
};

const selectConfidenceMask = (masks = [], labels = []) => {
  if (!Array.isArray(masks) || masks.length <= 0) return null;
  const labeledPersonIndex = resolveLabeledPersonIndex(labels);
  if (labeledPersonIndex >= 0 && masks[labeledPersonIndex]) {
    return { mask: masks[labeledPersonIndex], invert: false };
  }

  const labeledBackgroundIndex = Array.isArray(labels)
    ? labels.findIndex((label) => /background/i.test(String(label || '')))
    : -1;
  if (labeledBackgroundIndex >= 0 && masks[labeledBackgroundIndex]) {
    return { mask: masks[labeledBackgroundIndex], invert: true };
  }

  let bestMask = masks[0];
  let bestStats = getConfidenceMaskStats(bestMask);
  masks.forEach((mask) => {
    const stats = getConfidenceMaskStats(mask);
    if (stats.separation > bestStats.separation) {
      bestStats = stats;
      bestMask = mask;
    }
  });
  return { mask: bestMask, invert: bestStats.edgeAverage > bestStats.centerAverage };
};

const smoothStep = (edge0, edge1, value) => {
  const x = Math.max(0, Math.min(1, (value - edge0) / Math.max(0.001, edge1 - edge0)));
  return x * x * (3 - (2 * x));
};

const buildConfidenceMaskImageData = (mask, {
  invert = false,
  previousAlpha = null,
  smoothing = DEFAULT_MASK_SMOOTHING,
} = {}) => {
  const width = Math.max(1, Math.round(mask.width || 0));
  const height = Math.max(1, Math.round(mask.height || 0));
  const source = mask.getAsFloat32Array();
  const imageData = new ImageData(width, height);
  const nextAlpha = new Uint8ClampedArray(width * height);
  const canBlendPrevious = previousAlpha instanceof Uint8ClampedArray && previousAlpha.length === nextAlpha.length;
  const previousWeight = Math.max(0, Math.min(0.9, Number(smoothing) || 0));
  const currentWeight = 1 - previousWeight;
  for (let sourceIndex = 0, outputIndex = 0; sourceIndex < source.length; sourceIndex += 1, outputIndex += 4) {
    const rawValue = Math.max(0, Math.min(1, Number(source[sourceIndex]) || 0));
    const value = invert ? 1 - rawValue : rawValue;
    const foregroundStrength = Math.pow(
      smoothStep(FOREGROUND_ALPHA_LOW, FOREGROUND_ALPHA_HIGH, value),
      FOREGROUND_ALPHA_GAMMA,
    );
    const alpha = Math.round(
      canBlendPrevious
        ? (previousAlpha[sourceIndex] * previousWeight) + (foregroundStrength * 255 * currentWeight)
        : foregroundStrength * 255,
    );
    nextAlpha[sourceIndex] = alpha;
    imageData.data[outputIndex] = 255;
    imageData.data[outputIndex + 1] = 255;
    imageData.data[outputIndex + 2] = 255;
    imageData.data[outputIndex + 3] = alpha;
  }
  return { imageData, alpha: nextAlpha };
};

const countCategoryArea = (source, width, box) => {
  const counts = new Map();
  let total = 0;
  for (let y = box.top; y < box.bottom; y += 1) {
    for (let x = box.left; x < box.right; x += 1) {
      const category = source[y * width + x] || 0;
      counts.set(category, (counts.get(category) || 0) + 1);
      total += 1;
    }
  }
  return { counts, total };
};

const resolveCategoryForegroundIndex = (mask, labels = []) => {
  const labeledPersonIndex = resolveLabeledPersonIndex(labels);
  if (labeledPersonIndex >= 0) return labeledPersonIndex;
  const width = Math.max(1, Math.round(mask.width || 0));
  const height = Math.max(1, Math.round(mask.height || 0));
  const source = mask.getAsUint8Array();
  const boxes = getMaskSampleBoxes(width, height);
  const center = countCategoryArea(source, width, boxes.center);
  const edgeCounts = new Map();
  let edgeTotal = 0;
  boxes.edges.forEach((box) => {
    const area = countCategoryArea(source, width, box);
    area.counts.forEach((count, category) => {
      edgeCounts.set(category, (edgeCounts.get(category) || 0) + count);
    });
    edgeTotal += area.total;
  });
  const categories = new Set([...center.counts.keys(), ...edgeCounts.keys()]);
  let bestCategory = 1;
  let bestScore = -Infinity;
  categories.forEach((category) => {
    const centerShare = (center.counts.get(category) || 0) / Math.max(1, center.total);
    const edgeShare = (edgeCounts.get(category) || 0) / Math.max(1, edgeTotal);
    const score = centerShare - edgeShare;
    if (score > bestScore) {
      bestScore = score;
      bestCategory = category;
    }
  });
  return bestCategory;
};

const buildCategoryMaskImageData = (mask, personClassIndex, previousAlpha = null) => {
  const width = Math.max(1, Math.round(mask.width || 0));
  const height = Math.max(1, Math.round(mask.height || 0));
  const source = mask.getAsUint8Array();
  const imageData = new ImageData(width, height);
  const nextAlpha = new Uint8ClampedArray(width * height);
  const canBlendPrevious = previousAlpha instanceof Uint8ClampedArray && previousAlpha.length === nextAlpha.length;
  for (let sourceIndex = 0, outputIndex = 0; sourceIndex < source.length; sourceIndex += 1, outputIndex += 4) {
    const targetAlpha = source[sourceIndex] === personClassIndex ? 255 : 0;
    const alpha = Math.round(
      canBlendPrevious
        ? (previousAlpha[sourceIndex] * DEFAULT_MASK_SMOOTHING) + (targetAlpha * (1 - DEFAULT_MASK_SMOOTHING))
        : targetAlpha,
    );
    nextAlpha[sourceIndex] = alpha;
    imageData.data[outputIndex] = 255;
    imageData.data[outputIndex + 1] = 255;
    imageData.data[outputIndex + 2] = 255;
    imageData.data[outputIndex + 3] = alpha;
  }
  return { imageData, alpha: nextAlpha };
};

export const isVideoBackgroundBlurSupported = canUseVideoBackgroundProcessing;

export const createVideoBackgroundBlurProcessor = async ({
  sourceStream,
  frameRate = DEFAULT_FRAME_RATE,
  segmentationFrameRate = DEFAULT_SEGMENTATION_FRAME_RATE,
  segmentationMaxSizePx = DEFAULT_SEGMENTATION_MAX_SIZE_PX,
  blurRadiusPx = DEFAULT_BLUR_RADIUS_PX,
  maskFeatherPx = DEFAULT_MASK_FEATHER_PX,
} = {}) => {
  if (!canUseVideoBackgroundProcessing()) {
    throw new Error('Background blur is not supported by this browser');
  }

  const sourceVideoTrack = sourceStream?.getVideoTracks?.()[0] || null;
  if (!sourceVideoTrack) {
    throw new Error('Camera track is required for background blur');
  }

  const sourceVideoStream = new MediaStream([sourceVideoTrack]);
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.autoplay = true;
  video.srcObject = sourceVideoStream;
  await video.play().catch(() => null);
  await waitForVideoReady(video);

  const segmenter = await loadSelfieSegmenter();
  const segmenterLabels = typeof segmenter.getLabels === 'function' ? segmenter.getLabels() : [];
  const outputCanvas = document.createElement('canvas');
  const analysisCanvas = document.createElement('canvas');
  const foregroundCanvas = document.createElement('canvas');
  const rawMaskCanvas = document.createElement('canvas');
  const maskCanvas = document.createElement('canvas');
  const outputContext = outputCanvas.getContext('2d', { alpha: false });
  const analysisContext = analysisCanvas.getContext('2d');
  const foregroundContext = foregroundCanvas.getContext('2d');
  const rawMaskContext = rawMaskCanvas.getContext('2d');
  const maskContext = maskCanvas.getContext('2d');
  if (!outputContext || !analysisContext || !foregroundContext || !rawMaskContext || !maskContext) {
    throw new Error('Canvas rendering is unavailable for background blur');
  }

  resizeCanvas(outputCanvas, video.videoWidth, video.videoHeight);
  resizeCanvas(foregroundCanvas, video.videoWidth, video.videoHeight);
  resizeCanvas(maskCanvas, video.videoWidth, video.videoHeight);

  const outputStream = outputCanvas.captureStream(Math.max(1, Math.min(30, Number(frameRate) || DEFAULT_FRAME_RATE)));
  const processedVideoTrack = outputStream.getVideoTracks()[0] || null;
  if (!processedVideoTrack) {
    throw new Error('Unable to create processed video track');
  }

  const audioTracks = sourceStream?.getAudioTracks?.() || [];
  const previewStream = new MediaStream([...audioTracks, processedVideoTrack]);
  let stopped = false;
  let animationFrameId = null;
  let lastFrameAt = 0;
  let lastSegmentationAt = 0;
  let previousMaskAlpha = null;
  let hasUsableMask = false;
  const frameIntervalMs = 1000 / Math.max(1, Math.min(30, Number(frameRate) || DEFAULT_FRAME_RATE));
  const segmentationIntervalMs =
    1000 / Math.max(1, Math.min(20, Number(segmentationFrameRate) || DEFAULT_SEGMENTATION_FRAME_RATE));

  const updateMask = (width, height, timestamp) => {
    const analysisSize = getAnalysisSize(width, height, segmentationMaxSizePx);
    resizeCanvas(analysisCanvas, analysisSize.width, analysisSize.height);
    analysisContext.clearRect(0, 0, analysisSize.width, analysisSize.height);
    analysisContext.drawImage(video, 0, 0, analysisSize.width, analysisSize.height);

    const result = segmenter.segmentForVideo(analysisCanvas, timestamp);
    const categoryMask = result?.categoryMask || null;
    const confidenceMasks = Array.isArray(result?.confidenceMasks) ? result.confidenceMasks : [];
    try {
      const selectedConfidenceMask = selectConfidenceMask(confidenceMasks, segmenterLabels);
      const maskBuild = selectedConfidenceMask
        ? buildConfidenceMaskImageData(selectedConfidenceMask.mask, {
            invert: selectedConfidenceMask.invert,
            previousAlpha: previousMaskAlpha,
          })
        : categoryMask
          ? buildCategoryMaskImageData(
              categoryMask,
              resolveCategoryForegroundIndex(categoryMask, segmenterLabels),
              previousMaskAlpha,
            )
          : null;
      if (!maskBuild) {
        hasUsableMask = false;
        return;
      }

      previousMaskAlpha = maskBuild.alpha;
      resizeCanvas(rawMaskCanvas, maskBuild.imageData.width, maskBuild.imageData.height);
      rawMaskContext.putImageData(maskBuild.imageData, 0, 0);

      maskContext.clearRect(0, 0, width, height);
      maskContext.save();
      maskContext.filter = `blur(${Math.max(0, Number(maskFeatherPx) || DEFAULT_MASK_FEATHER_PX)}px)`;
      maskContext.drawImage(rawMaskCanvas, 0, 0, width, height);
      maskContext.restore();
      hasUsableMask = true;
    } finally {
      try {
        categoryMask?.close?.();
      } catch {}
      confidenceMasks.forEach((mask) => {
        try {
          mask?.close?.();
        } catch {}
      });
    }
  };

  const drawFrame = (timestamp) => {
    if (stopped) return;
    animationFrameId = window.requestAnimationFrame(drawFrame);
    if (timestamp - lastFrameAt < frameIntervalMs) return;
    lastFrameAt = timestamp;
    if (video.readyState < 2 || video.videoWidth <= 0 || video.videoHeight <= 0) return;

    resizeCanvas(outputCanvas, video.videoWidth, video.videoHeight);
    resizeCanvas(foregroundCanvas, video.videoWidth, video.videoHeight);
    resizeCanvas(maskCanvas, video.videoWidth, video.videoHeight);
    const width = outputCanvas.width;
    const height = outputCanvas.height;
    try {
      if (!hasUsableMask || timestamp - lastSegmentationAt >= segmentationIntervalMs) {
        lastSegmentationAt = timestamp;
        updateMask(width, height, timestamp);
      }

      outputContext.save();
      outputContext.filter = `blur(${Math.max(0, Number(blurRadiusPx) || DEFAULT_BLUR_RADIUS_PX)}px)`;
      outputContext.drawImage(video, 0, 0, width, height);
      outputContext.restore();

      if (!hasUsableMask) {
        outputContext.filter = 'none';
        outputContext.drawImage(video, 0, 0, width, height);
        return;
      }
      foregroundContext.clearRect(0, 0, width, height);
      foregroundContext.globalCompositeOperation = 'source-over';
      foregroundContext.drawImage(video, 0, 0, width, height);
      foregroundContext.globalCompositeOperation = 'destination-in';
      foregroundContext.drawImage(maskCanvas, 0, 0, width, height);
      foregroundContext.globalCompositeOperation = 'source-over';
      outputContext.drawImage(foregroundCanvas, 0, 0, width, height);
    } catch {
      outputContext.filter = 'none';
      outputContext.drawImage(video, 0, 0, width, height);
    }
  };

  animationFrameId = window.requestAnimationFrame(drawFrame);

  return {
    effect: VIDEO_BACKGROUND_EFFECT_BLUR,
    stream: previewStream,
    videoTrack: processedVideoTrack,
    stop: () => {
      stopped = true;
      if (animationFrameId != null) {
        window.cancelAnimationFrame(animationFrameId);
      }
      try {
        video.pause();
        video.srcObject = null;
      } catch {}
      processedVideoTrack.stop();
    },
  };
};
